import type { CSSProperties } from 'react'
import { cn } from '@/lib/cn'
import type { MediaObject } from '@/lib/types'

/**
 * One image or one video, or nothing.
 *
 * A media item can exist with no file attached — `id`, `alt` and `caption` are
 * populated while the file-derived fields are null — so the guard is on `url`
 * specifically. Guarding on the object being absent renders a broken image;
 * guarding on `alt` hides images that simply have no alt text.
 *
 * **Served straight from the CMS, not through Next's optimiser.** The panel
 * already writes four conversions per upload — thumb, md, lg, full, in the
 * format the media settings name — and serves them as a `srcset`. Handing
 * those to the optimiser is a second compression pass over an image that was
 * already compressed, for widths the browser can pick between by itself.
 *
 * It also broke. The optimiser fetches a same-origin path through Next's own
 * request handling, and Next only serves what was in `public/` when the
 * process started — `public/storage` being a symlink into the media library
 * does not change that. So every upload made after a deploy came back "not a
 * valid image" until the next restart, while everything seeded before it
 * worked. An image pipeline that depends on when the server last booted is not
 * a pipeline.
 *
 * The design uses every image as a cover fill inside a positioned box, which
 * is why the positioning is here rather than at each call site.
 */
export function Media({
    media,
    alt,
    className,
    style,
    priority = false,
    sizes = '(max-width: 1024px) 100vw, 60vw',
}: {
    media: MediaObject | null | undefined
    alt?: string | null
    className?: string
    style?: CSSProperties
    priority?: boolean
    sizes?: string
}) {
    if (media == null || media.url === null) {
        return null
    }

    const fill = cn('absolute inset-0 block h-full w-full object-cover', className)

    if (isVideo(media)) {
        return <Clip media={media} className={fill} style={style} priority={priority} />
    }

    return (
        <img
            src={media.url}
            // `sizes` without a `srcset` is meaningless, and an item whose
            // conversions have not run yet has only the upload itself.
            srcSet={media.srcset ?? undefined}
            sizes={media.srcset === null ? undefined : sizes}
            alt={alt ?? media.alt ?? ''}
            width={media.width ?? undefined}
            height={media.height ?? undefined}
            loading={priority ? 'eager' : 'lazy'}
            fetchPriority={priority ? 'high' : undefined}
            decoding="async"
            className={fill}
            style={style}
        />
    )
}

/** Whether this item is something to play rather than something to draw. */
function isVideo(media: MediaObject): boolean {
    return media.mime_type?.startsWith('video/') === true && media.original_url !== null
}

/**
 * A case that moves.
 *
 * Muted, looping and inline, which is the only combination a browser will
 * start on its own — and the only one that belongs in a card anyway: a card
 * that plays sound because it scrolled into view is a card people close the
 * tab on. It carries no controls for the same reason. The clip is decoration
 * around the words beside it; the case itself is the page it links to.
 *
 * `poster` is the frame the CMS pulled out of the file — that is what `url`
 * is for a video, while `original_url` is the video itself. When there is no
 * frame the two are the same file, because `url` falls back to the upload: a
 * poster attribute pointing at an MP4 is ignored, but an `<img>` pointing at
 * one is a broken-image icon, so the still is only drawn when there is a
 * still.
 *
 * Playback is left to CSS on purpose: `motion-reduce:hidden` on the video and
 * the poster underneath it means a visitor who asked for less motion gets the
 * still, without this component having to read a media query, hydrate, and
 * decide again on every render.
 */
function Clip({
    media,
    className,
    style,
    priority,
}: {
    media: MediaObject
    className: string
    style?: CSSProperties
    priority: boolean
}) {
    const poster = media.url === media.original_url ? null : media.url

    return (
        <>
            {/* The frame, for a reader who asked for less motion and for the
             * moment before the video has anything to show. */}
            {poster === null ? null : (
                <img
                    src={poster}
                    alt={media.alt ?? ''}
                    loading={priority ? 'eager' : 'lazy'}
                    decoding="async"
                    className={className}
                    style={style}
                />
            )}
            <video
                src={media.original_url ?? undefined}
                poster={poster ?? undefined}
                autoPlay
                muted
                loop
                playsInline
                preload={priority ? 'auto' : 'metadata'}
                aria-label={media.alt ?? undefined}
                className={cn(className, 'motion-reduce:hidden')}
                style={style}
            />
        </>
    )
}
